import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { UploadSimple, LinkSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Convert common non-direct image URLs to direct-image URLs.
 *  - Google Drive share link (drive.google.com/file/d/<id>/view or open?id=<id>)
 *    → https://drive.google.com/uc?export=view&id=<id>
 *  - Google Photos: not directly rewritable, return as-is (will likely 403).
 */
function normalizeExternalImageUrl(url) {
  if (!url || typeof url !== "string") return url;
  const s = url.trim();
  // Google Drive — various share formats:
  //   drive.google.com/file/d/<ID>/view?...
  //   drive.google.com/file/d/<ID>/preview
  //   drive.google.com/open?id=<ID>
  //   drive.google.com/uc?id=<ID>&export=...
  //   docs.google.com/uc?id=<ID>
  // NOTE: uc?export=view is unreliable (redirects to HTML confirmation for
  // large files). `thumbnail?id=...&sz=w<width>` renders the image reliably
  // for any size and is CORS-safe for <img>.
  const gid = s.match(/(?:drive|docs)\.google\.com\/(?:file\/d\/|(?:open|uc|thumbnail)\?[^#]*id=)([a-zA-Z0-9_-]{10,})/i);
  if (gid && gid[1]) {
    return `https://drive.google.com/thumbnail?id=${gid[1]}&sz=w1024`;
  }
  return s;
}

/**
 * Resolve any stored path/URL into an absolute src usable in <img>.
 *  - "/api/files/..." → prepended with BACKEND_URL
 *  - Google Drive share link → converted to direct-image URL
 *  - "data:..." or http(s) → returned as-is (after gdrive normalisation)
 *  - "" / null → empty string
 */
export function resolveImageUrl(value) {
  if (!value) return "";
  const v = value.trim();
  if (v.startsWith("data:")) return v;
  if (v.startsWith("http")) return normalizeExternalImageUrl(v);
  if (v.startsWith("/")) return `${BACKEND_URL}${v}`;
  // Legacy raw storage path (e.g. "nitisoft-auction/registrations/uuid.jpg")
  // stored by the early public-registration upload endpoint — route it through
  // the backend file server so old records keep rendering.
  if (v.startsWith(`${"nitisoft-auction"}/`) || /^[\w-]+\/(registrations|photos)\//.test(v)) {
    return `${BACKEND_URL}/api/files/${v}`;
  }
  return v;
}

/**
 * ImageInput: two modes
 *  1) URL — user pastes any URL
 *  2) Upload — file is uploaded to backend object storage; server returns URL
 * Emits value string via onChange.
 */
export default function ImageInput({ value, onChange, label = "Image", testId, maxKB = 5120, aspect = "aspect-square", acceptGif = false, dark = false }) {
  const [mode, setMode] = useState(value && !value.startsWith("http") && !value.startsWith("data:") && value.startsWith("/api") ? "upload" : "url");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const preview = resolveImageUrl(value);

  // When `acceptGif` is set the picker also accepts animated GIFs and short
  // announcement videos (.3gp/.mp4/.webm/.mov) for SOLD/UNSOLD overlays.
  const acceptAttr = acceptGif
    ? "image/*,image/gif,video/3gpp,video/mp4,video/webm,video/quicktime,.gif,.3gp,.3gpp,.mp4,.webm,.mov"
    : "image/*";
  const videoExts = [".3gp", ".3gpp", ".mp4", ".webm", ".mov"];
  const isVideoFile = (f) => {
    const t = (f.type || "").toLowerCase();
    if (t.startsWith("video/")) return true;
    const n = (f.name || "").toLowerCase();
    return videoExts.some((e) => n.endsWith(e));
  };
  const isVideoUrl = (u) => {
    if (!u) return false;
    const s = u.split("?")[0].toLowerCase();
    return videoExts.some((e) => s.endsWith(e));
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okType = acceptGif
      ? (file.type.startsWith("image/") || isVideoFile(file))
      : file.type.startsWith("image/");
    if (!okType) { toast.error(acceptGif ? "Choose an image, GIF or short video" : "Please choose an image file"); return; }
    if (file.size > maxKB * 1024) {
      toast.error(`File must be under ${(maxKB / 1024).toFixed(1)} MB`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      setMode("upload");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clear = () => { onChange(""); if (fileRef.current) fileRef.current.value = ""; };

  const previewIsVideo = isVideoUrl(value);

  return (
    <div data-testid={testId}>
      <label className={`label-mono block mb-2 ${dark ? "!text-slate-400" : ""}`}>{label}</label>
      <div className="flex gap-1 mb-2 text-xs">
        <button type="button" onClick={() => setMode("url")} className={`px-3 py-1 rounded border font-bold uppercase tracking-widest ${mode === "url" ? (dark ? "border-blue-500 text-blue-300 bg-blue-500/15" : "border-blue-500 text-blue-700 bg-blue-500/10") : (dark ? "border-white/15 text-slate-400 hover:text-slate-200" : "border-gray-200 text-gray-500")}`}>
          <LinkSimple size={12} className="inline mr-1" /> URL
        </button>
        <button type="button" onClick={() => setMode("upload")} className={`px-3 py-1 rounded border font-bold uppercase tracking-widest ${mode === "upload" ? (dark ? "border-blue-500 text-blue-300 bg-blue-500/15" : "border-blue-500 text-blue-700 bg-blue-500/10") : (dark ? "border-white/15 text-slate-400 hover:text-slate-200" : "border-gray-200 text-gray-500")}`}>
          <UploadSimple size={12} className="inline mr-1" /> Upload
        </button>
      </div>

      <div className="flex items-stretch gap-3">
        {value ? (
          <div className={`${aspect} w-20 flex-shrink-0 rounded overflow-hidden border ${dark ? "border-white/15 bg-white/5" : "border-gray-200 bg-white"} relative`}>
            {previewIsVideo ? (
              <video src={preview} className="w-full h-full object-cover" muted autoPlay loop playsInline />
            ) : (
              <img src={preview} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
            )}
            <button type="button" onClick={clear} className="absolute top-1 right-1 bg-black/70 rounded p-0.5 hover:bg-red-500/80 text-white">
              <X size={12} />
            </button>
          </div>
        ) : (
          <div className={`${aspect} w-20 flex-shrink-0 rounded border border-dashed grid place-items-center text-xs ${dark ? "border-white/20 text-slate-500 bg-white/[0.03]" : "border-gray-200 text-gray-400"}`}>—</div>
        )}
        <div className="flex-1 min-w-0">
          {mode === "url" ? (
            <input
              type="url"
              placeholder="https://..."
              value={value && (value.startsWith("http") || value.startsWith("/api")) ? value : ""}
              onChange={(e) => onChange(e.target.value)}
              data-testid={testId ? `${testId}-url` : undefined}
              className={dark
                ? "w-full bg-white/5 border border-white/15 rounded-md px-3.5 py-2.5 min-h-[46px] text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition"
                : "w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none"}
            />
          ) : (
            <div>
              <input ref={fileRef} type="file" accept={acceptAttr} onChange={handleFile} className="hidden" data-testid={testId ? `${testId}-file` : undefined} />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                className={dark
                  ? "w-full border border-white/15 rounded-md px-3 min-h-[46px] text-xs font-bold uppercase tracking-widest text-slate-300 hover:border-blue-500 hover:text-blue-300 flex items-center justify-center gap-2 transition"
                  : "btn-ghost text-xs w-full flex items-center justify-center gap-2 !py-2"}>
                <UploadSimple size={14} /> {busy ? "Uploading…" : "Choose from device"}
              </button>
              <div className={`text-[10px] mt-1 ${dark ? "text-slate-500" : "text-gray-500"}`}>Max {(maxKB / 1024).toFixed(1)} MB · {acceptGif ? "PNG / JPG / WEBP / GIF / 3GP / MP4" : "PNG / JPG / WEBP"}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
